import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, KpiCard } from "@/components/common/UI";
import { REGION_FLAG } from "@/lib/format";
import { Building2, MapPin, Store, Clock } from "lucide-react";

export default function DCsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const load = () => api.get("/distribution-centers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (d) => {
    try { await api.patch(`/distribution-centers/${d.id}`, { active: !d.active }); toast.success(`${d.name} ${d.active ? "deactivated" : "activated"}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Spinner />;
  return (
    <div data-testid="dcs-page">
      <PageHeader title="Distribution Centers" subtitle="Third-party logistics hubs that receive and route store orders" />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <KpiCard label="Distribution centers" value={rows.length} icon={Building2} tone="slate" />
        <KpiCard label="Active centers" value={rows.filter((d) => d.active).length} icon={Building2} tone="emerald" delay={60} />
        <KpiCard label="Open orders" value={rows.reduce((a, d) => a + d.pending_orders, 0)} hint="Pending + approved" icon={Clock} tone="amber" delay={120} />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="dc-grid">
        {rows.map((d, i) => (
          <div key={d.id} style={{ animationDelay: `${i * 50}ms` }} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md animate-in fade-in slide-in-from-bottom-3 fill-mode-both" data-testid={`dc-card-${d.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-display text-lg font-bold text-slate-900">{REGION_FLAG[d.region]} {d.name}</p><p className="font-mono text-xs text-slate-400">Code: {d.code} · {d.region}</p></div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${d.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{d.active ? "Active" : "Inactive"}</span>
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{d.address}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Assigned stores</p><p className="font-display text-2xl font-extrabold"><Store className="mr-1 inline h-4 w-4 text-slate-400" />{d.assigned_stores}</p></div>
              <div className="rounded-xl bg-amber-50 p-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Open orders</p><p className="font-display text-2xl font-extrabold text-amber-800">{d.pending_orders}</p></div>
            </div>
            {user.role === "admin" && <button data-testid={`toggle-dc-${d.code}`} onClick={() => toggle(d)} className="mt-4 text-xs font-semibold text-slate-500 hover:text-red-600">{d.active ? "Deactivate" : "Activate"}</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
